import React, { useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

function ImageLightbox({ isOpen, onClose, imageUrl, imageAlt, images, currentIndex, onNavigate }) {
  // Handle escape key to close
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (images && images.length > 1) {
      if (e.key === 'ArrowLeft') {
        onNavigate('prev');
      } else if (e.key === 'ArrowRight') {
        onNavigate('next');
      }
    }
  }, [onClose, images, onNavigate]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Use current image from array if available, otherwise use single imageUrl
  const displayUrl = images && images.length > 0 ? images[currentIndex]?.url : imageUrl;
  const displayAlt = images && images.length > 0 ? images[currentIndex]?.alt || imageAlt : imageAlt;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate('prev');
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate('next');
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center"
      onClick={handleBackdropClick}
      data-testid="image-lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-all duration-200"
        aria-label="Close lightbox"
        data-testid="lightbox-close"
      >
        <X size={28} className="text-white" />
      </button>

      {/* Navigation arrows for gallery mode */}
      {images && images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-all duration-200"
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} className="text-white" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-all duration-200"
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} className="text-white" />
          </button>
        </>
      )}

      {/* Image container */}
      <div className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={displayUrl}
          alt={displayAlt || 'Full size image'}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          data-testid="lightbox-image"
        />
      </div>

      {/* Image counter for gallery mode */}
      {images && images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-lg font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Caption */}
      {displayAlt && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-2xl text-center">
          <p className="text-white text-lg bg-black bg-opacity-50 px-6 py-3 rounded-lg">
            {displayAlt}
          </p>
        </div>
      )}

      {/* Instructions hint */}
      <div className="absolute bottom-6 right-6 text-white text-sm opacity-60">
        Press ESC to close{images && images.length > 1 ? ' • Use arrow keys to navigate' : ''}
      </div>
    </div>
  );
}

export default ImageLightbox;
